import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { track } from '../utils/analytics';

/* ── Single source of truth — add new roles here ────────────────────────── */
export const NAV_ROLES = [
  { rgb:'16,185,129', color:'#10B981', label:'RevSec',         sub:'AI governance, security & compliance',    badge:'Live', path:'/ai-ciso',        icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.7"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg> },
  { rgb:'0,212,170',  color:'#00D4AA', label:'AI Architect',   sub:'Legacy apps, AI-agent ready',             badge:'Live', path:'/ai-architect',   icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00D4AA" strokeWidth="1.7"><rect x="2" y="3" width="6" height="6" rx="1"/><rect x="16" y="3" width="6" height="6" rx="1"/><rect x="9" y="15" width="6" height="6" rx="1"/><path d="M5 9v3h14V9"/><path d="M12 12v3"/></svg> },
  { rgb:'245,158,11', color:'#F59E0B', label:'AI DBA',         sub:'Database migration & modernisation',      badge:'Live', path:'/ai-dba',         icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.7"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg> },
  { rgb:'139,92,246', color:'#8B5CF6', label:'AI QA',          sub:'Automated quality assurance & testing',   badge:'Live', path:'/ai-qa',          icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.7"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
  { rgb:'124,58,237', color:'#7C3AED', label:'Custom AI Role', sub:'Build a bespoke AI role for your domain', badge:'', path:'/custom', icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.7"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg> },
];

/* ── Desktop dropdown item ──────────────────────────────────────────────── */
function DropItem({ rgb, color, icon, label, sub, badge, path }) {
  const [hov, setHov] = useState(false);
  const navigate = useNavigate();
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={()=>navigate(path)}
      style={{ display:'flex', alignItems:'center', gap:11, padding:'9px 11px', borderRadius:9, cursor:'pointer', background:hov?`rgba(${rgb},.08)`:'transparent', transition:'background .15s' }}>
      <div style={{ width:34, height:34, borderRadius:8, background:`rgba(${rgb},.1)`, border:`1px solid rgba(${rgb},.22)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:1 }}>
          <span style={{ fontSize:13.5, fontWeight:600, color:'#1A0A2E' }}>{label}</span>
          {badge && <span style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', padding:'2px 7px', borderRadius:100, background:`rgba(${rgb},.12)`, color, border:`1px solid rgba(${rgb},.25)` }}>{badge}</span>}
        </div>
        <div style={{ fontSize:11.5, color:'#3D2F6B' }}>{sub}</div>
      </div>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#6B5F8A" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
    </div>
  );
}

/* ── Mobile drawer ──────────────────────────────────────────────────────── */
function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const go = (path) => { navigate(path); onClose(); };

  return (
    <>
      {open && <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,.6)', backdropFilter:'blur(4px)' }}/>}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, zIndex:401, width:'min(320px, 85vw)', background:'rgba(237,234,246,.99)', borderLeft:'1px solid rgba(0,0,0,.1)', transform:open?'translateX(0)':'translateX(100%)', transition:'transform .3s cubic-bezier(.4,0,.2,1)', overflowY:'auto', display:'flex', flexDirection:'column', backdropFilter:'blur(24px)' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 20px 16px' }}>
          <Logo/>
          <button onClick={onClose} style={{ background:'rgba(0,0,0,.05)', border:'1px solid rgba(0,0,0,.1)', borderRadius:8, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(10,6,20,.7)" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div style={{ height:1, background:'rgba(0,0,0,.07)', margin:'0 20px 4px' }}/>

        {/* Roles section */}
        <div style={{ padding:'16px 12px 8px' }}>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'#00A882', padding:'0 8px', marginBottom:10 }}>AI Roles</div>
          {NAV_ROLES.slice(0,-1).map((r,i) => (
            <div key={i} onClick={() => go(r.path)}
              style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 8px', borderRadius:10, cursor:'pointer', marginBottom:2, transition:'background .15s' }}
              onMouseEnter={e => e.currentTarget.style.background=`rgba(${r.rgb},.07)`}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              <div style={{ width:36, height:36, borderRadius:9, background:`rgba(${r.rgb},.12)`, border:`1px solid rgba(${r.rgb},.25)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{r.icon}</div>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                  <span style={{ fontSize:14, fontWeight:600, color:'#1A0A2E' }}>{r.label}</span>
                  {r.badge && <span style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', padding:'2px 6px', borderRadius:100, background:`rgba(${r.rgb},.14)`, color:r.color, border:`1px solid rgba(${r.rgb},.28)` }}>{r.badge}</span>}
                </div>
                <div style={{ fontSize:11.5, color:'#3D2F6B' }}>{r.sub}</div>
              </div>
            </div>
          ))}

          <div style={{ height:1, background:'rgba(0,0,0,.07)', margin:'8px 8px 8px' }}/>

          <div onClick={() => go('/custom')}
            style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 8px', borderRadius:10, cursor:'pointer', transition:'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(124,58,237,.06)'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}
          >
            <div style={{ width:36, height:36, borderRadius:9, background:'rgba(124,58,237,.1)', border:'1px solid rgba(124,58,237,.22)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{NAV_ROLES[4].icon}</div>
            <div>
              <div style={{ fontSize:14, fontWeight:500, color:'#2D1B5E', marginBottom:2 }}>Custom AI Role</div>
              <div style={{ fontSize:11.5, color:'#3D2F6B' }}>Build a bespoke AI role</div>
            </div>
          </div>
        </div>

        <div style={{ height:1, background:'rgba(0,0,0,.07)', margin:'4px 20px' }}/>

        {/* Other pages */}
        <div style={{ padding:'12px 20px', display:'flex', flexDirection:'column' }}>
          {[['Pricing','/pricing'],['About','/about']].map(([label,path]) => (
            <button key={label} onClick={() => go(path)}
              style={{ background:'none', border:'none', borderBottom:'1px solid rgba(0,0,0,.07)', cursor:'pointer', padding:'13px 0', fontSize:15, fontWeight:400, color:'#4A3F6B', fontFamily:"'DM Sans',system-ui,sans-serif", textAlign:'left', transition:'color .2s' }}
              onMouseEnter={e => e.currentTarget.style.color='#1A0A2E'}
              onMouseLeave={e => e.currentTarget.style.color='#4A3F6B'}
            >{label}</button>
          ))}
        </div>

        {/* Contact info strip */}
        <div style={{ margin:'0 20px', padding:'14px 16px', borderRadius:10, background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.18)' }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(0,160,130,.85)', marginBottom:8, fontFamily:"'DM Sans',system-ui,sans-serif" }}>Get in touch</div>
          <a href="mailto:sales@revupai.com" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none', color:'#1A0A2E', fontSize:13.5, fontFamily:"'DM Sans',system-ui,sans-serif", fontWeight:500 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00A882" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            sales@revupai.com
          </a>
        </div>

        {/* CTA */}
        <div style={{ padding:'20px 20px 36px', marginTop:'auto' }}>
          <button onClick={() => { track('book_demo', { location: 'nav_mobile' }); go('/contact'); }}
            style={{ width:'100%', background:'#00D4AA', color:'#09060F', border:'none', cursor:'pointer', padding:'15px', borderRadius:10, fontSize:15, fontWeight:600, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:'0 0 24px rgba(0,212,170,.3)', transition:'all .25s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow='0 0 40px rgba(0,212,170,.5)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow='0 0 24px rgba(0,212,170,.3)'}
          >Book a Demo →</button>
        </div>
      </div>
    </>
  );
}

/* ── Hamburger ──────────────────────────────────────────────────────────── */
function Hamburger({ open, onClick }) {
  return (
    <button onClick={onClick} style={{ background:'rgba(0,0,0,.05)', border:'1px solid rgba(0,0,0,.1)', borderRadius:9, width:40, height:40, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:5, cursor:'pointer', padding:0 }}>
      <span style={{ display:'block', width:18, height:1.5, background:'rgba(10,6,20,.7)', borderRadius:2, transition:'all .25s', transform: open?'rotate(45deg) translate(4.5px,4.5px)':'none' }}/>
      <span style={{ display:'block', width:18, height:1.5, background:'rgba(10,6,20,.7)', borderRadius:2, transition:'all .25s', opacity:open?0:1 }}/>
      <span style={{ display:'block', width:18, height:1.5, background:'rgba(10,6,20,.7)', borderRadius:2, transition:'all .25s', transform: open?'rotate(-45deg) translate(4.5px,-4.5px)':'none' }}/>
    </button>
  );
}

/* ══ NAV ════════════════════════════════════════════════════════════════════ */
export default function Nav() {
  const [scrolled,   setScrolled]  = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const lnk = {
    background:'none', border:'none', cursor:'pointer', padding:'8px 12px',
    borderRadius:8, fontSize:13.5, fontWeight:500, color:'rgba(10,6,20,.55)',
    fontFamily:"'DM Sans',system-ui,sans-serif", transition:'color .2s',
    display:'flex', alignItems:'center', gap:4,
  };
  const isActive = (path) => location.pathname === path;

  return (
    <>
      <style>{`
        .nav-root { padding: 0 clamp(24px, 5vw, 100px) !important; }
        @media (max-width: 1100px) { .nav-root { padding: 0 32px !important; } }
        @media (max-width: 700px)  { .nav-root { padding: 0 20px !important; } }
        /* Hide text links + contact link on mobile, show hamburger */
        @media (max-width: 700px)  { .nav-center-links { display: none !important; } }
        @media (max-width: 700px)  { .nav-contact-link { display: none !important; } }
        @media (max-width: 700px)  { .nav-demo-btn     { display: none !important; } }
        @media (max-width: 700px)  { .nav-hamburger    { display: flex !important; } }
        /* Dropdown */
        .ndw { position: relative; }
        .ndw .drop { display:none; position:absolute; top:calc(100% + 6px); left:50%; transform:translateX(-50%); z-index:300; }
        .ndw:hover .drop { display:block; }
        .drop-inner { background:rgba(237,234,246,.98); border:1px solid rgba(0,0,0,.1); borderRadius:14px; padding:6px; width:300px; box-shadow:0 16px 48px rgba(0,0,0,.15); backdrop-filter:blur(16px); border-radius:14px; }
      `}</style>

      <nav className="nav-root" style={{ position:'fixed', top:0, left:0, right:0, zIndex:200, height:64, display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(237,234,246,.97)', backdropFilter:'blur(20px)', borderBottom:'1px solid rgba(0,0,0,.08)', boxShadow:'0 1px 24px rgba(0,0,0,.06)' }}>

        {/* Logo */}
        <div onClick={() => navigate('/')} style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
          <Logo/>
          <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:16, fontWeight:700, letterSpacing:'-0.3px', lineHeight:1, userSelect:'none' }}>
            <span style={{ color:'#1A0A2E' }}>RevUp</span><span style={{ color:'#7C3AED' }}>AI</span>
          </span>
        </div>

        {/* ── Desktop centre links ── */}
        <div className="nav-center-links" style={{ display:'flex', gap:2, alignItems:'center' }}>

          {/* AI Roles dropdown */}
          <div className="ndw">
            {(() => {
              const activeRole = NAV_ROLES.find(r => r.path === location.pathname);
              return (
                <button style={{ ...lnk, color: activeRole ? '#1A0A2E' : 'rgba(10,6,20,.55)', fontWeight: activeRole ? 600 : 500 }}
                  onMouseEnter={e=>e.currentTarget.style.color='#1A0A2E'}
                  onMouseLeave={e=>e.currentTarget.style.color= activeRole ? '#1A0A2E' : 'rgba(10,6,20,.55)'}>
                  {activeRole ? (
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ display:'flex', alignItems:'center', justifyContent:'center', width:18, height:18, borderRadius:5, background:`rgba(${activeRole.rgb},.12)`, border:`1px solid rgba(${activeRole.rgb},.3)` }}>{activeRole.icon}</span>
                      <span style={{ color:`rgba(${activeRole.rgb},1)`, fontWeight:700 }}>{activeRole.label}</span>
                    </span>
                  ) : 'AI Roles'}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity:.5 }}><path d="M6 9l6 6 6-6"/></svg>
                </button>
              );
            })()}
            <div className="drop">
              <div className="drop-inner" style={{ background:'rgba(237,234,246,.98)', border:'1px solid rgba(0,0,0,.1)', borderRadius:14, padding:6, width:300, boxShadow:'0 16px 48px rgba(0,0,0,.15)', backdropFilter:'blur(16px)' }}>
                <div style={{ padding:'10px 12px 8px', borderBottom:'1px solid rgba(0,0,0,.07)', marginBottom:4 }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'#00A882' }}>Operated AI Roles</div>
                  <div style={{ fontSize:11.5, color:'#3D2F6B', marginTop:2 }}>Prebuilt · Expert-Operated · Day 1 Ready</div>
                </div>
                {NAV_ROLES.slice(0,-1).map((r,i) => <DropItem key={i} {...r}/>)}
                <div style={{ height:1, background:'rgba(0,0,0,.07)', margin:'6px 4px' }}/>
                <DropItem {...NAV_ROLES[NAV_ROLES.length-1]}/>
              </div>
            </div>
          </div>

          {/* Pricing + About + Blog */}
          {[['Pricing','/pricing'],['About','/about'],['Blog','/blog']].map(([l, path]) => (
            <button key={l} style={{ ...lnk, color: isActive(path) ? 'rgba(10,6,20,.95)' : 'rgba(10,6,20,.55)', fontWeight: isActive(path) ? 600 : 500 }}
              onClick={() => navigate(path)}
              onMouseEnter={e => e.currentTarget.style.color='#1A0A2E'}
              onMouseLeave={e => e.currentTarget.style.color= isActive(path) ? 'rgba(10,6,20,.95)' : 'rgba(10,6,20,.55)'}
            >{l}</button>
          ))}
        </div>

        {/* ── Right — Contact text link + CTA + hamburger ── */}
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>

          {/* Contact text link — visible desktop/tablet, hidden mobile */}
          <button className="nav-contact-link"
            onClick={() => navigate('/contact')}
            style={{ ...lnk, color: isActive('/contact') ? 'rgba(10,6,20,.95)' : 'rgba(10,6,20,.55)', fontWeight: isActive('/contact') ? 600 : 500 }}
            onMouseEnter={e => e.currentTarget.style.color='#1A0A2E'}
            onMouseLeave={e => e.currentTarget.style.color= isActive('/contact') ? 'rgba(10,6,20,.95)' : 'rgba(10,6,20,.55)'}
          >Contact</button>

          {/* CTA */}
          <button className="nav-demo-btn" onClick={() => { track('book_demo', { location: 'nav' }); navigate('/contact'); }}
            style={{ background:'#00D4AA', color:'#09060F', border:'none', cursor:'pointer', padding:'9px 22px', borderRadius:8, fontSize:14, fontWeight:600, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:'0 0 20px rgba(0,212,170,.25)', transition:'all .25s ease', whiteSpace:'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow='0 0 36px rgba(0,212,170,.5)'; e.currentTarget.style.transform='translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow='0 0 20px rgba(0,212,170,.25)'; e.currentTarget.style.transform='none'; }}
          >Book a Demo →</button>

          {/* Hamburger — hidden on desktop via CSS */}
          <div className="nav-hamburger" style={{ display:'none' }}>
            <Hamburger open={drawerOpen} onClick={() => setDrawerOpen(o => !o)}/>
          </div>
        </div>
      </nav>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}/>
    </>
  );
}
